import React from "react";
import Creatable from "react-select/creatable";
import { FormattedMessage } from "react-intl";
import Util from "../../util/Util";

export default class ComboBox extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      option: null,
    };

    // If we have an initial default option - set it
    if (this.props.defaultOption) {
      // Si defaultOption est déjà formaté (a value et label), on l'utilise directement
      if (
        typeof this.props.defaultOption === "object" &&
        this.props.defaultOption.value !== undefined &&
        this.props.defaultOption.label !== undefined
      ) {
        this.state = {
          option: this.props.defaultOption,
        };
      } else {
        // Sinon c'est un ID, on cherche l'option et on la formate
        var option = this.getOptionById(this.props.defaultOption);

        if (option) {
          this.state = {
            option: this.formatOption(option),
          };
        }
      }
    }
  }

  componentDidUpdate(prevProps) {
    // Update option when defaultOption prop changes
    // Comparaison plus robuste pour détecter les changements
    const prevValue =
      typeof prevProps.defaultOption === "object"
        ? prevProps.defaultOption?.value
        : prevProps.defaultOption;
    const currentValue =
      typeof this.props.defaultOption === "object"
        ? this.props.defaultOption?.value
        : this.props.defaultOption;

    if (prevValue !== currentValue) {
      if (this.props.defaultOption) {
        // Si defaultOption est déjà formaté (a value et label), on l'utilise directement
        if (
          typeof this.props.defaultOption === "object" &&
          this.props.defaultOption.value !== undefined &&
          this.props.defaultOption.label !== undefined
        ) {
          this.setState({ option: this.props.defaultOption });
        } else {
          // Sinon c'est un ID, on cherche l'option et on la formate
          var option = this.getOptionById(this.props.defaultOption);
          if (option) {
            this.setState({ option: this.formatOption(option) });
          }
        }
      } else {
        this.setState({ option: null });
      }
    }
  }

  getOptionById(optionId) {
    for (let option of this.props.options) {
      if (option._id === optionId) return option;
    }
  }

  formatOptions(options) {
    let formattedOptions = [];
    for (let option of options) {
      // Si l'option a déjà value et label, on la garde telle quelle
      if (option.value !== undefined && option.label !== undefined) {
        formattedOptions.push(option);
      } else {
        // Sinon on la formate
        formattedOptions.push(this.formatOption(option));
      }
    }
    return formattedOptions;
  }

  formatOption(option) {
    return { value: option._id, label: option.name };
  }

  createOption(optionName) {
    if (Util.emptyString(optionName)) return;

    // Convertir en majuscules si la prop upperCase est activée
    const finalOptionName = this.props.upperCase
      ? optionName.toUpperCase()
      : optionName;

    var successCallback = (newOption) => {
      this.setState({ option: this.formatOption(newOption) });
      this.props.onChange(newOption._id);
    };

    this.props.onCreateOption({ name: finalOptionName }, successCallback);
  }

  onChange(option) {
    // Only update internal state if component is uncontrolled (no value prop)
    if (this.props.value === undefined) {
      this.setState({ option: option });
    }

    if (this.props.onChange) {
      // Si l'option a une propriété data, passer l'option complète
      // Sinon passer juste la value (comportement par défaut)
      if (option && option.data !== undefined) {
        this.props.onChange(option);
      } else {
        this.props.onChange(option ? option.value : null);
      }
    }
  }

  onBlur() {
    if (!this.state.option) return;

    if (this.props.onBlur) this.props.onBlur(this.state.option.value);
  }

  isAValidOption(optionId) {
    // Si optionId est un objet avec value, extraire la value
    const idToCheck =
      typeof optionId === "object" && optionId?.value !== undefined
        ? optionId.value
        : optionId;

    for (let option of this.props.options) {
      // Support pour les options avec _id (format standard)
      if (idToCheck === option._id) {
        return true;
      }
      // Support pour les options pré-formatées avec value
      if (option.value !== undefined && idToCheck === option.value) {
        return true;
      }
    }

    return false;
  }

  render() {
    var options = this.formatOptions(this.props.options);

    // Si une prop 'value' est fournie, utiliser celle-ci (composant contrôlé)
    // Sinon utiliser le state interne (composant non-contrôlé)
    var optionValue =
      this.props.value !== undefined ? this.props.value : this.state.option;

    if (
      this.props.value === undefined &&
      !Util.emptyString(this.state.option) &&
      !this.isAValidOption(this.props.defaultOption)
    ) {
      optionValue = null;
    }

    // Styles par défaut si non fournis
    const defaultStyles = {
      control: (provided, state) => ({
        ...provided,
        minHeight: "56px",
        height: "56px",
        backgroundColor: "#ffffff",
        borderColor: state.isFocused ? "#3b82f6" : "#d1d5db",
        "&:hover": {
          borderColor: "#9ca3af",
        },
        cursor: "text",
      }),
      valueContainer: (provided) => ({
        ...provided,
        padding: "8px 16px",
        height: "52px",
        display: "flex",
        alignItems: "center",
      }),
      input: (provided) => ({
        ...provided,
        fontSize: "1rem",
        fontWeight: "600",
        margin: "0px",
        padding: "0px",
        color: "#000000",
        caretColor: "transparent",
        border: "none",
        outline: "none",
        boxShadow: "none",
      }),
      placeholder: (provided) => ({
        ...provided,
        fontSize: "1rem",
        fontWeight: "500",
        color: "#9ca3af",
      }),
      singleValue: (provided) => ({
        ...provided,
        fontSize: "1rem",
        fontWeight: "600",
        color: "#000000",
      }),
    };

    return (
      <Creatable
        menuPlacement={this.props.placement || "auto"}
        createOptionPosition="first"
        onCreateOption={(value) => this.createOption(value)}
        onChange={(value) => this.onChange(value)}
        onBlur={() => this.onBlur()}
        onInputChange={(inputValue, actionMeta) => {
          // Appeler le callback externe si fourni
          if (this.props.onInputChange) {
            this.props.onInputChange(inputValue, actionMeta);
          }

          // Convertir en majuscules si demandé
          if (this.props.upperCase && actionMeta.action === "input-change") {
            return inputValue.toUpperCase();
          }
          return inputValue;
        }}
        isDisabled={this.props.disabled}
        isLoading={this.props.isLoading}
        isClearable={this.props.isClearable}
        value={optionValue}
        options={options}
        styles={this.props.styles || defaultStyles}
        components={this.props.components}
        noOptionsMessage={(val) => this.props.hideNoOptionsMessage ? null : <FormattedMessage id="No.Options" />}
        formatCreateLabel={(val) => (
          <FormattedMessage id="Create.Val" values={{ val: val }} />
        )}
        placeholder={this.props.placeholder || <FormattedMessage id="Select" />}
      />
    );
  }
}
